import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

const tierStyles = {
  1: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  2: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  3: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

function useTierLabels() {
  const { t } = useTranslation();
  return {
    1: t('tier.tier1'),
    2: t('tier.tier2'),
    3: t('tier.tier3'),
  };
}

function useTierInfo() {
  const { t } = useTranslation();
  return [
    {
      tier: 1,
      description: t('tier.tier1Desc'),
      examples: t('tier.tier1Examples'),
      teaching: t('tier.tier1Teaching'),
    },
    {
      tier: 2,
      description: t('tier.tier2Desc'),
      examples: t('tier.tier2Examples'),
      teaching: t('tier.tier2Teaching'),
    },
    {
      tier: 3,
      description: t('tier.tier3Desc'),
      examples: t('tier.tier3Examples'),
      teaching: t('tier.tier3Teaching'),
    },
  ];
}

export default function TierBadge({ tier, size = 'md', showTooltip = false, className }) {
  const { t } = useTranslation();
  const tierLabels = useTierLabels();
  const tierInfo = useTierInfo();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState('below');
  const badgeRef = useRef(null);
  const timeoutRef = useRef(null);

  if (!tier) return null;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };

  const handleMouseEnter = () => {
    if (!showTooltip) return;
    clearTimeout(timeoutRef.current);

    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setPosition(spaceBelow < 280 ? 'above' : 'below');
    }

    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  if (!showTooltip) {
    return (
      <span
        className={cn(
          'font-medium rounded',
          tierStyles[tier] || tierStyles[2],
          sizeClasses[size],
          className
        )}
      >
        {tierLabels[tier] || t('tier.tierLabel', { tier })}
      </span>
    );
  }

  return (
    <span
      ref={badgeRef}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        className={cn(
          'font-medium rounded cursor-help',
          tierStyles[tier] || tierStyles[2],
          sizeClasses[size],
          className
        )}
      >
        {tierLabels[tier] || t('tier.tierLabel', { tier })}
      </span>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 w-80 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4',
            position === 'below' ? 'top-full mt-2' : 'bottom-full mb-2',
            'left-1/2 -translate-x-1/2'
          )}
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={handleMouseLeave}
        >
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            {t('tier.beckTitle')}
          </h4>
          <div className="space-y-3">
            {tierInfo.map((info) => (
              <div
                key={info.tier}
                className={cn(
                  'rounded-md p-2.5 text-xs',
                  info.tier === tier
                    ? 'ring-2 ring-offset-1 dark:ring-offset-gray-800'
                    : 'opacity-70',
                  info.tier === 1 && 'bg-emerald-50 dark:bg-emerald-900/20',
                  info.tier === 1 && info.tier === tier && 'ring-emerald-400',
                  info.tier === 2 && 'bg-amber-50 dark:bg-amber-900/20',
                  info.tier === 2 && info.tier === tier && 'ring-amber-400',
                  info.tier === 3 && 'bg-rose-50 dark:bg-rose-900/20',
                  info.tier === 3 && info.tier === tier && 'ring-rose-400',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={cn(
                    'font-semibold',
                    info.tier === 1 && 'text-emerald-700 dark:text-emerald-400',
                    info.tier === 2 && 'text-amber-700 dark:text-amber-400',
                    info.tier === 3 && 'text-rose-700 dark:text-rose-400',
                  )}>
                    {t('tier.tierLabel', { tier: info.tier })}
                  </span>
                  {info.tier === 2 && (
                    <span className="text-[10px] font-medium text-amber-600 dark:text-amber-300 uppercase tracking-wide">
                      {t('tier.sweetSpot')}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-1">{info.description}</p>
                <p className="text-gray-500 dark:text-gray-400 italic">{t('tier.examplesLabel', { examples: info.examples })}</p>
                <p className="text-gray-500 dark:text-gray-400 mt-1">{info.teaching}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}
